import React, { useRef, forwardRef, useState } from 'react';
import DatePicker from 'react-datepicker';
import cn from 'classnames';

import Input from 'components/common/Form/Input';
import { CalendarIcon } from 'components/common/icons';
import './react-datepicker.scss';
import styles from './DatePicker.module.scss';

import { formatDateToUTC } from 'helpers/formatter';
import CustomTooltip from '../../CustomTooltip';
import _ from 'lodash';

function Datepicker({
    name = 'date',
    value,
    label,
    className,
    withoutCustomInput,
    fullWidth,
    maxDate,
    onChange,
    onChangeField,
    error,
    readOnly,
    showTimeSelect,
    customInput,
    minDate,
    withDateFormatToUTC,
    timeIntervals,
    isChanged = true,
    containerRef,
    customTooltipText,
    customInputClassName,
    containerDataPickerClassName,
    inputClassName,
    id,
    labelClassName,
    onBlur = _.noop,
}) {
    const handleChange = (value) => {
        const result = withDateFormatToUTC ? formatDateToUTC(value) : value;
        if (onChangeField && onChangeField !== _.noop) {
            onChangeField({ target: { name, value: result } });
        } else {
            onChange && onChange(result);
        }
        onBlur();
    };

    const DefaultCustomInput = (
        <Input
            showBtn
            fullWidth={fullWidth}
            withoutClearBtn
            label={label}
            name={name}
            value={value}
            error={error}
            onChangeField={onChangeField}
            inputId={`${id}_datepickerInput`}
            wrapperClassName={customInputClassName}
            labelClassName={labelClassName}
            inputClassName={inputClassName}
            rightIcon={({ className, ...props }) => (
                <CalendarIcon {...props} className={cn(className, styles.calendarIcon)} onClick={onCalendarClick} />
            )}
        />
    );

    const CustomInputToRender = customInput || DefaultCustomInput;

    const innerRef = useRef(null);
    const attachRef = (el) => {
        if (!el) return;
        innerRef.current = el;
        if (typeof containerRef === 'function') {
            containerRef(el.input.offsetParent);
        } else if (containerRef) {
            containerRef.current = el.input.offsetParent;
        }
    };
    const datepicker = (
        <DatePicker
            className={className}
            customInput={withoutCustomInput ? false : CustomInputToRender}
            popperClassName={cn(styles.popperDefault, { withTimeSelect: showTimeSelect })}
            popperModifiers={{
                preventOverflow: {
                    enabled: true,
                },
            }}
            ref={attachRef}
            showYearDropdown
            showMonthDropdown
            showTimeSelect={showTimeSelect}
            dateFormat={isChanged ? 'MM/dd/yyyy' : ' '}
            maxDate={maxDate}
            minDate={minDate}
            onChange={handleChange}
            selected={value}
            timeIntervals={timeIntervals}
            timeFormat='HH:mm'
            yearDropdownItemNumber={name && name.toLowerCase() === 'birthday' ? new Date().getYear() : null}
            readOnly={readOnly}
            placeholderText='mm/dd/yyyy'
        />
    );

    const onCalendarClick = (e) => {
        const isOpen = innerRef.current.isCalendarOpen();
        if (!isOpen) innerRef.current.handleFocus();
    };

    const [tooltipHovered, setTooltipHovered] = useState(false);
    const datepickerWithTooltipContainer = (
        <div>
            <div onMouseEnter={() => setTooltipHovered(true)} onMouseLeave={() => setTooltipHovered(false)}>
                {datepicker}
            </div>
            <div className={styles.customTooltipContainer}>
                <CustomTooltip
                    text={customTooltipText}
                    displayed={tooltipHovered && !innerRef.current.isCalendarOpen()}
                />
            </div>
        </div>
    );

    if (!withoutCustomInput) return customTooltipText ? datepickerWithTooltipContainer : datepicker;

    return (
        <div className={cn(styles.containerDatepicker, containerDataPickerClassName)}>
            <div className={cn(styles.datepickerLabel, error && styles.errorLabel)}>{label || 'Date'}</div>
            {customTooltipText ? datepickerWithTooltipContainer : datepicker}
            {error && <p>{error}</p>}
            <CalendarIcon onClick={onCalendarClick} className={styles.calendarIcon} />
        </div>
    );
}

export default forwardRef((props, ref) => <Datepicker {...props} containerRef={ref} />);
